/**
 * Messages Notification Badge - Badge flotante para notificar mensajes docente
 * SOLO visible en móvil - en desktop el sidebar ya muestra la alerta
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageSquare, X, ChevronRight } from 'lucide-react';
import { useUnreadMessages } from '../hooks/useUnreadMessages';

const MessagesNotificationBadge = () => {
  const navigate = useNavigate();
  const { unreadCount } = useUnreadMessages();
  const [dismissed, setDismissed] = React.useState(false);
  const [isExpanded, setIsExpanded] = React.useState(false);

  // No mostrar si no hay mensajes, fue dismiss, o es desktop
  if (unreadCount === 0 || dismissed) return null;

  return (
    <>
      {/* SOLO MÓVIL - Ocultar en desktop (md: >= 768px) */}
      <div className="md:hidden fixed bottom-20 right-4 z-40">
        <div className="relative">
          {/* Badge compacto (estado colapsado) */}
          {!isExpanded ? (
            <button
              onClick={() => setIsExpanded(true)}
              className="bg-white text-gray-900 rounded-full shadow-lg hover:shadow-xl transition-all active:scale-95 p-3 flex items-center gap-2 border border-gray-200 animate-pulse-soft"
            >
              <div className="relative">
                <MessageSquare className="w-5 h-5 text-purple-600" strokeWidth={2} />
                {/* Contador badge pequeño */}
                <div className="absolute -top-2 -right-2 bg-red-500 text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </div>
              </div>
            </button>
          ) : (
            /* Badge expandido con información */
            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden min-w-[280px] animate-scale-in">
              {/* Header */}
              <div className="bg-gradient-to-r from-purple-50 to-purple-100/50 px-4 py-3 border-b border-purple-100">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center">
                      <MessageSquare className="w-4 h-4 text-white" strokeWidth={2} />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900">Mensajes nuevos</h3>
                      <p className="text-xs text-gray-600">
                        {unreadCount} {unreadCount === 1 ? 'mensaje' : 'mensajes'}
                      </p>
                    </div>
                  </div>
                  {/* Botón cerrar */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsExpanded(false);
                      setTimeout(() => setDismissed(true), 200);
                    }}
                    className="p-1 hover:bg-purple-200/50 rounded-lg transition-colors"
                  >
                    <X className="w-4 h-4 text-gray-600" />
                  </button>
                </div>
              </div>

              {/* Body */}
              <div className="p-4">
                <p className="text-xs text-gray-600 mb-3 leading-relaxed">
                  Tu docente ha enviado feedback sobre tus laboratorios
                </p>
                
                <button
                  onClick={() => navigate('/epl/messages')}
                  className="w-full bg-purple-600 text-white rounded-lg px-4 py-2.5 text-sm font-medium hover:bg-purple-700 active:bg-purple-800 transition-colors flex items-center justify-center gap-2 group"
                >
                  <span>Ver mensajes</span>
                  <ChevronRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" strokeWidth={2} />
                </button>

                {/* Link para minimizar */}
                <button
                  onClick={() => setIsExpanded(false)}
                  className="w-full mt-2 text-xs text-gray-500 hover:text-gray-700 py-1"
                >
                  Minimizar
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Estilos para animaciones */}
      <style jsx>{`
        @keyframes pulse-soft {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
        @keyframes scale-in {
          0% { transform: scale(0.9); opacity: 0; }
          100% { transform: scale(1); opacity: 1; }
        }
        .animate-pulse-soft {
          animation: pulse-soft 2s ease-in-out infinite;
        }
        .animate-scale-in {
          animation: scale-in 0.2s ease-out;
        }
      `}</style>
    </>
  );
};

export default MessagesNotificationBadge;
